import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  Select,
  FormRow,
  Stack,
  Input
} from '@efast_public/fjd-component-library';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import {
  CreateServiceTemplateRequest,
  ServiceTemplate
} from '../api/govos.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GovosApiRoutes,
  govosClient,
  invalidateServiceTemplatesCache
} from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { useTemplateNameValidation } from '../hooks/useTemplateNameValidation';

interface FinalTemplateConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  startAppId: string | number;
  templateId?: string;
  templateName?: string;
  authenticationTemplates: ServiceTemplate[];
  deliveryTemplates: ServiceTemplate[];
  paymentTemplates: ServiceTemplate[];
  schriftformTemplates: ServiceTemplate[];
  totalTemplates?: ServiceTemplate[];
  existingNames?: string[];
  configuration?: TemplateConfigurationFormData;
}

interface TemplateConfigurationFormData {
  authTemplateId: string;
  deliveryTemplateId: string;
  paymentTemplateId: string;
  schriftformTemplateId: string;
}

interface FullFormData extends TemplateConfigurationFormData {
  name: string;
}

export function FinalTemplateConfigurationModal({
  isOpen,
  onClose,
  onSuccess,
  startAppId,
  templateId,
  templateName,
  authenticationTemplates,
  deliveryTemplates,
  paymentTemplates,
  schriftformTemplates,
  totalTemplates,
  existingNames,
  configuration
}: FinalTemplateConfigurationModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);
  const isCreateMode = templateId === undefined;

  const { templateNameError, validateTemplateName, clearTemplateNameError } =
    useTemplateNameValidation({
      currentTemplateName: isCreateMode ? undefined : templateName,
      existingNames,
      t,
      validationKeyPrefix: 'FinalTemplateConfigurationModal'
    });

  const validationSchema = CustomJoi.object<FullFormData>({
    name: isCreateMode
      ? CustomJoi.string()
          .required()
          .custom((value, helpers) => {
            if (
              existingNames
                ?.map((n) => n.toLowerCase())
                .includes(value.toLowerCase())
            ) {
              return helpers.error('name.duplicate');
            }
            return value;
          })
          .messages({
            'name.duplicate': t(
              'FinalTemplateConfigurationModal.validation.nameDuplicate'
            )
          })
      : CustomJoi.string().allow('').optional(),
    authTemplateId: CustomJoi.string().allow('').optional(),
    deliveryTemplateId: CustomJoi.string().allow('').optional(),
    paymentTemplateId: CustomJoi.string().allow('').optional(),
    schriftformTemplateId: CustomJoi.string().allow('').optional()
  })
    .custom((value, helpers) => {
      if (
        !value.authTemplateId &&
        !value.deliveryTemplateId &&
        !value.paymentTemplateId &&
        !value.schriftformTemplateId
      ) {
        return helpers.error('object.atLeastOne');
      }
      return value;
    })
    .messages({
      'object.atLeastOne': t(
        'FinalTemplateConfigurationModal.validation.atLeastOne'
      )
    });

  const { control, handleSubmit, formState, register, setValue, watch } =
    useForm<FullFormData>({
      defaultValues: {
        name: isCreateMode ? '' : (templateName ?? ''),
        ...(configuration ?? {
          authTemplateId: '',
          deliveryTemplateId: '',
          paymentTemplateId: '',
          schriftformTemplateId: ''
        })
      },
      resolver: joiResolver(validationSchema),
      mode: 'onChange'
    });

  const { mutate: updateTemplate, isPending: isUpdatePending } = useMutation({
    mutationFn: async (payload: FullFormData) =>
      notifyUserFromRequest(
        () =>
          govosClient.put(
            GovosApiRoutes.serviceTemplate(startAppId, templateId!),
            {
              name: payload.name || undefined,
              configuration: JSON.stringify({
                authTemplateId: payload.authTemplateId,
                deliveryTemplateId: payload.deliveryTemplateId,
                paymentTemplateId: payload.paymentTemplateId,
                schriftformTemplateId: payload.schriftformTemplateId
              })
            }
          ),
        {
          successMessage: t('FinalTemplateConfigurationModal.successMessage'),
          errorMessage: t('FinalTemplateConfigurationModal.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
      onSuccess?.();
      onClose();
    }
  });

  const { mutate: createTemplate, isPending: isCreatePending } = useMutation({
    mutationFn: async (payload: CreateServiceTemplateRequest) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(
            GovosApiRoutes.serviceTemplates(startAppId),
            payload
          ),
        {
          successMessage: t('FinalTemplateConfigurationModal.successMessage'),
          errorMessage: t('FinalTemplateConfigurationModal.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
      onSuccess?.();
      onClose();
    }
  });

  const isPending = isUpdatePending || isCreatePending;

  const onSubmit = (data: FullFormData) => {
    if (isCreateMode) {
      const { name, ...configData } = data;
      createTemplate({
        name,
        serviceType: 'total',
        configuration: JSON.stringify(configData)
      });
    } else {
      updateTemplate(data);
    }
  };

  const handleBaseTemplateChange = (baseId: string) => {
    const baseTemplate = totalTemplates?.find((t) => t.id == baseId);
    if (baseTemplate?.configuration) {
      const config = JSON.parse(
        baseTemplate.configuration
      ) as TemplateConfigurationFormData;
      setValue('authTemplateId', config.authTemplateId ?? '', {
        shouldValidate: true
      });
      setValue('deliveryTemplateId', config.deliveryTemplateId ?? '', {
        shouldValidate: true
      });
      setValue('paymentTemplateId', config.paymentTemplateId ?? '', {
        shouldValidate: true
      });
      setValue('schriftformTemplateId', config.schriftformTemplateId ?? '', {
        shouldValidate: true
      });
    }
  };

  const watchedAuth = watch('authTemplateId');
  const watchedDelivery = watch('deliveryTemplateId');
  const watchedPayment = watch('paymentTemplateId');
  const watchedSchriftform = watch('schriftformTemplateId');

  const authentifizierungOptions = authenticationTemplates.map((tpl) => ({
    value: String(tpl.id),
    label: tpl.name
  }));

  const zustellwegOptions = deliveryTemplates.map((tpl) => ({
    value: String(tpl.id),
    label: tpl.name
  }));

  const bezahldienstOptions = paymentTemplates.map((tpl) => ({
    value: String(tpl.id),
    label: tpl.name
  }));

  const schriftformOptions = schriftformTemplates.map((tpl) => ({
    value: String(tpl.id),
    label: tpl.name
  }));

  const totalTemplateOptions = (totalTemplates ?? []).map((tpl) => ({
    value: String(tpl.id),
    label: tpl.name
  }));

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t(
            isCreateMode
              ? 'FinalTemplateConfigurationModal.titleCreate'
              : 'FinalTemplateConfigurationModal.title'
          )}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing="l" orientation="vertical">
            <FormRow
              label={t('FinalTemplateConfigurationModal.fields.name')}
              validationMessage={
                formState.errors.name?.message || templateNameError
              }
              variant={
                formState.errors.name || templateNameError ? 'error' : undefined
              }
            >
              <Input
                {...register('name')}
                placeholder={t(
                  'FinalTemplateConfigurationModal.fields.namePlaceholder'
                )}
                style={{ width: '100%' }}
                onChange={(e) => {
                  register('name').onChange(e);
                  clearTemplateNameError();
                }}
              />
            </FormRow>

            {isCreateMode && totalTemplateOptions.length > 0 && (
              <FormRow
                label={t('CreateServiceTemplateModal.description.total')}
              >
                <Select
                  onChange={(value) => {
                    const selected = value.at(0)?.value;
                    if (selected) handleBaseTemplateChange(selected);
                  }}
                  options={totalTemplateOptions}
                  placeholder={t(
                    'FinalTemplateConfigurationModal.fields.baseTemplatePlaceholder'
                  )}
                  style={{ width: '100%' }}
                />
              </FormRow>
            )}

            <Stack spacing="l" orientation="vertical">
              <Controller
                name="authTemplateId"
                control={control}
                render={({ field }) => (
                  <FormRow
                    label={t(
                      'FinalTemplateConfigurationModal.fields.authentifizierung'
                    )}
                  >
                    <Select
                      selectedOptions={authentifizierungOptions.filter(
                        (opt) => opt.value === watchedAuth
                      )}
                      onChange={(value) =>
                        setValue('authTemplateId', value.at(0)?.value ?? '', {
                          shouldValidate: true,
                          shouldDirty: true
                        })
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      options={authentifizierungOptions}
                      placeholder={
                        authentifizierungOptions.length === 0
                          ? t('select.noOptionsAvailable')
                          : t('select.placeholder')
                      }
                      disabled={authentifizierungOptions.length === 0}
                      style={{ width: '100%' }}
                    />
                  </FormRow>
                )}
              />

              <Controller
                name="deliveryTemplateId"
                control={control}
                render={({ field }) => (
                  <FormRow
                    label={t(
                      'FinalTemplateConfigurationModal.fields.zustellweg'
                    )}
                  >
                    <Select
                      selectedOptions={zustellwegOptions.filter(
                        (opt) => opt.value === watchedDelivery
                      )}
                      onChange={(value) =>
                        setValue(
                          'deliveryTemplateId',
                          value.at(0)?.value ?? '',
                          {
                            shouldValidate: true,
                            shouldDirty: true
                          }
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      options={zustellwegOptions}
                      placeholder={
                        zustellwegOptions.length === 0
                          ? t('select.noOptionsAvailable')
                          : t('select.placeholder')
                      }
                      disabled={zustellwegOptions.length === 0}
                      style={{ width: '100%' }}
                    />
                  </FormRow>
                )}
              />

              <Controller
                name="paymentTemplateId"
                control={control}
                render={({ field }) => (
                  <FormRow
                    label={t(
                      'FinalTemplateConfigurationModal.fields.bezahldienst'
                    )}
                  >
                    <Select
                      selectedOptions={bezahldienstOptions.filter(
                        (opt) => opt.value === watchedPayment
                      )}
                      onChange={(value) =>
                        setValue(
                          'paymentTemplateId',
                          value.at(0)?.value ?? '',
                          {
                            shouldValidate: true,
                            shouldDirty: true
                          }
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      options={bezahldienstOptions}
                      placeholder={
                        bezahldienstOptions.length === 0
                          ? t('select.noOptionsAvailable')
                          : t('select.placeholder')
                      }
                      disabled={bezahldienstOptions.length === 0}
                      style={{ width: '100%' }}
                    />
                  </FormRow>
                )}
              />

              <Controller
                name="schriftformTemplateId"
                control={control}
                render={({ field }) => (
                  <FormRow
                    label={t(
                      'FinalTemplateConfigurationModal.fields.schriftform'
                    )}
                  >
                    <Select
                      selectedOptions={schriftformOptions.filter(
                        (opt) => opt.value === watchedSchriftform
                      )}
                      onChange={(value) =>
                        setValue(
                          'schriftformTemplateId',
                          value.at(0)?.value ?? '',
                          {
                            shouldValidate: true,
                            shouldDirty: true
                          }
                        )
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      options={schriftformOptions}
                      placeholder={
                        schriftformOptions.length === 0
                          ? t('select.noOptionsAvailable')
                          : t('select.placeholder')
                      }
                      disabled={schriftformOptions.length === 0}
                      style={{ width: '100%' }}
                    />
                  </FormRow>
                )}
              />
            </Stack>
          </Stack>
        </form>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ flex: 1 }}
          >
            {t('FinalTemplateConfigurationModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (!isCreateMode) {
                const currentName = watch('name');
                if (currentName && currentName.trim()) {
                  if (!validateTemplateName(currentName)) {
                    return;
                  }
                }
              }
              handleSubmit((data) => {
                onSubmit(data);
              })();
            }}
            disabled={
              (!formState.isValid && (isCreateMode || formState.isDirty)) ||
              isPending
            }
            style={{ flex: 1 }}
          >
            {t('FinalTemplateConfigurationModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
